import React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, Package, Upload, Settings2, Store, Image, FileSpreadsheet, Users, LogOut, Trash2, Gem, Truck, History, Sparkles, Coins, Link2, Briefcase, UploadCloud, Menu, BookOpen } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const NAV = [
  { to: "/", end: true, icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
  { to: "/products", icon: Package, label: "Products", testid: "nav-products" },
  { to: "/upload", icon: Upload, label: "Upload", testid: "nav-upload" },
  { to: "/upload-designs", icon: UploadCloud, label: "Upload Designs", testid: "nav-upload-designs" },
  { to: "/designs", icon: Sparkles, label: "Designs", testid: "nav-designs" },
  // CS 22.0 -- Franchise Master Catalog (separate from DISTRIBUTION > Catalog)
  { to: "/master-catalog", icon: BookOpen, label: "Master Catalog", testid: "nav-master-catalog", roles: ["master_admin"] },
  { to: "/upload-history", icon: History, label: "Upload History", testid: "nav-upload-history" },
  { to: "/rates", icon: Settings2, label: "Rate Settings", testid: "nav-rates" },
  { to: "/shapes", icon: Gem, label: "Diamond Shapes", testid: "nav-shapes" },
  { to: "/franchises", icon: Store, label: "Franchises", testid: "nav-franchises" },
  { to: "/users", icon: Users, label: "Users", testid: "nav-users" },
  { to: "/photos", icon: Image, label: "Photos", testid: "nav-photos" },
  { to: "/reports", icon: FileSpreadsheet, label: "Reports", testid: "nav-reports" },
  { to: "/trash", icon: Trash2, label: "Trash", testid: "nav-trash" },
];

const DIST_NAV = [
  { to: "/distribution/catalog", icon: Sparkles, label: "Catalog", testid: "nav-dist-catalog" },
  { to: "/distribution/catalog-upload", icon: Upload, label: "Catalog Upload", testid: "nav-dist-upload" },
  { to: "/distribution/rates", icon: Coins, label: "Rate Settings", testid: "nav-dist-rates" },
  { to: "/distribution/links", icon: Link2, label: "Selection Links", testid: "nav-dist-links" },
  // CS 21.18 -- Distributors relocated into the DISTRIBUTION group.
  { to: "/distributors", icon: Truck, label: "Distributors", testid: "nav-distributors" },
  // CS 21.20 -- Distributor Stock upload (separate from Franchise Catalog Upload).
  // master_admin only -- ho_staff hitting this route sees a forbidden fallback.
  { to: "/distributor-upload", icon: UploadCloud, label: "Distributor Upload", testid: "nav-distributor-upload", roles: ["master_admin"] },
];

// CS 21.6 — Wholesale is now its own top-level section, peer to (not nested under) Distribution.
const WHOLESALE_NAV = [
  { to: "/wholesalers", icon: Briefcase, label: "Wholesalers", testid: "nav-wholesalers" },
];

// Mobile bottom bar: top-4 most-used items; everything else lives in the
// "More" sheet. Parity invariant: pinned(4) + sheet(NAV.slice(4) + DIST +
// WHOLESALE, role-filtered) === desktop sidebar items for every role.
const MOBILE_PINNED_COUNT = 4;

const roleFilter = (item, role) => !item.roles || item.roles.includes(role);

function BrandMark({ variant = "sidebar" }) {
  const [imgFailed, setImgFailed] = React.useState(false);
  const onErr = () => setImgFailed(true);
  if (variant === "sidebar") {
    return (
      <div className="flex flex-col items-center gap-2" data-testid="brand-mark">
        {!imgFailed ? (
          <img
            src="/brand/ashth_logo.png"
            alt="Ashth"
            onError={onErr}
            className="h-14 w-auto object-contain"
            style={{ filter: "brightness(0) invert(1) sepia(1) saturate(2) hue-rotate(5deg) brightness(1.15)" }}
          />
        ) : (
          <span className="font-serif text-4xl tracking-[0.3em] text-[#C9A84C]">APS</span>
        )}
        <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">Ashth Pricing System</span>
      </div>
    );
  }
  // mobile compact
  return !imgFailed ? (
    <img src="/brand/ashth_logo.png" alt="Ashth" onError={onErr} className="h-8 w-auto object-contain" />
  ) : (
    <span className="font-serif text-2xl tracking-[0.3em] text-[#0D1B2A]">APS</span>
  );
}

// One mobile sheet row. Closes the sheet on tap, highlights the active page.
function MoreItem({ item, onNavigate }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      data-testid={`mnav-${item.testid}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-md text-sm tracking-wide transition-colors ${
          isActive
            ? "bg-[#C9A84C]/10 text-[#0D1B2A] font-medium border-l-2 border-[#C9A84C]"
            : "text-slate-600 hover:text-[#0D1B2A] hover:bg-[#0D1B2A]/5"
        }`
      }
    >
      <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
      {item.label}
    </NavLink>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = React.useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const pinned = NAV.slice(0, MOBILE_PINNED_COUNT);
  const sheetMain = NAV.slice(MOBILE_PINNED_COUNT).filter((item) => roleFilter(item, user?.role));
  const sheetDist = DIST_NAV.filter((item) => roleFilter(item, user?.role));
  const sheetWholesale = WHOLESALE_NAV.filter((item) => roleFilter(item, user?.role));

  // Highlight "More" when the current page lives inside the sheet so the
  // user always has an active-state cue, whichever surface they're on.
  const moreActive = [...sheetMain, ...sheetDist, ...sheetWholesale].some((item) =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to),
  );

  const closeMore = () => setMoreOpen(false);

  return (
    <div className="min-h-screen flex bg-[#FAF8F3]">
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col bg-[#0D1B2A] text-white" data-testid="admin-sidebar">
        <div className="px-6 py-7 border-b border-white/10">
          <BrandMark variant="sidebar" />
        </div>
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {NAV.filter((item) => roleFilter(item, user?.role)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm tracking-wide transition-colors ${
                  isActive
                    ? "bg-[#C9A84C]/15 text-[#C9A84C] border-l-2 border-[#C9A84C]"
                    : "text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
          {/* Distribution Module sub-section */}
          <div className="pt-4 mt-3 border-t border-white/10">
            <p className="px-4 mb-2 text-[10px] uppercase tracking-[0.25em] text-white/40">Distribution</p>
            {DIST_NAV.filter((item) => !item.roles || item.roles.includes(user?.role)).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm tracking-wide transition-colors ${
                    isActive
                      ? "bg-[#C9A84C]/15 text-[#C9A84C] border-l-2 border-[#C9A84C]"
                      : "text-white/70 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <item.icon className="w-4 h-4" strokeWidth={1.5} />
                {item.label}
              </NavLink>
            ))}
          </div>

          {/* CS 21.6 — Wholesale Module (peer to Distribution) */}
          <div className="pt-4 mt-3 border-t border-white/10" data-testid="nav-wholesale-section">
            <p className="px-4 mb-2 text-[10px] uppercase tracking-[0.25em] text-white/40">Wholesale</p>
            {WHOLESALE_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm tracking-wide transition-colors ${
                    isActive
                      ? "bg-[#C9A84C]/15 text-[#C9A84C] border-l-2 border-[#C9A84C]"
                      : "text-white/70 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <item.icon className="w-4 h-4" strokeWidth={1.5} />
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
        <div className="px-6 py-5 border-t border-white/10">
          <div className="text-[10px] text-white/50 uppercase tracking-widest mb-1" data-testid="sidebar-role-label">{user?.role === "ho_staff" ? "HO Staff" : "Master Admin"}</div>
          <div className="text-sm font-medium truncate" data-testid="admin-user-email">{user?.email}</div>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="mt-4 flex items-center gap-2 text-xs text-white/60 hover:text-[#C9A84C] transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 left-0 right-0 z-40 glass-nav px-6 py-3 flex items-center justify-between">
        <BrandMark variant="mobile" />
        <button onClick={doLogout} className="text-xs text-slate-600">Sign out</button>
      </div>

      <main className="flex-1 min-w-0 pb-20 md:pb-0 pt-14 md:pt-0">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-12">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom bar: top-4 pinned items + "More". The full menu
          (role-filtered, same groups as desktop) lives in the sheet. */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#0D1B2A]/10 grid grid-cols-5" data-testid="mobile-bottom-bar">
        {pinned.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-2 text-[10px] uppercase tracking-wider ${
                isActive ? "text-[#C9A84C]" : "text-slate-500"
              }`
            }
          >
            <item.icon className="w-5 h-5 mb-0.5" strokeWidth={1.5} />
            {item.label}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          data-testid="nav-more-btn"
          aria-label="More menu items"
          className={`flex flex-col items-center justify-center py-2 text-[10px] uppercase tracking-wider ${
            moreActive ? "text-[#C9A84C]" : "text-slate-500"
          }`}
        >
          <Menu className="w-5 h-5 mb-0.5" strokeWidth={1.5} />
          More
        </button>
      </nav>

      {/* More sheet: every remaining desktop menu item, same groups, role-filtered. */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="md:hidden max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white px-2 pb-6"
          data-testid="nav-more-sheet"
        >
          <SheetHeader className="text-left px-4 pt-2 pb-3">
            <SheetTitle className="font-serif text-2xl text-[#0D1B2A]">Menu</SheetTitle>
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400">
              {user?.role === "ho_staff" ? "HO Staff" : "Master Admin"} · {user?.email}
            </p>
          </SheetHeader>
          <nav className="space-y-0.5">
            {sheetMain.map((item) => (
              <MoreItem key={item.to} item={item} onNavigate={closeMore} />
            ))}
            <div className="pt-4 mt-2 border-t border-[#0D1B2A]/10">
              <p className="px-4 mb-1.5 text-[10px] uppercase tracking-[0.25em] text-slate-400">Distribution</p>
              {sheetDist.map((item) => (
                <MoreItem key={item.to} item={item} onNavigate={closeMore} />
              ))}
            </div>
            <div className="pt-4 mt-2 border-t border-[#0D1B2A]/10">
              <p className="px-4 mb-1.5 text-[10px] uppercase tracking-[0.25em] text-slate-400">Wholesale</p>
              {sheetWholesale.map((item) => (
                <MoreItem key={item.to} item={item} onNavigate={closeMore} />
              ))}
            </div>
          </nav>
          <div className="px-4 pt-4 mt-2 border-t border-[#0D1B2A]/10">
            <button
              type="button"
              onClick={doLogout}
              data-testid="mnav-logout-btn"
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#C9A84C] transition-colors"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
